import PT from 'prop-types';
import React from 'react';

import Field from '~/components/Workflow/shared/Field';
import { useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import ListItems from '~/ui/components/ListItems/ListItems';
import Text from '~/ui/components/Text/Text';
import { testConditionalTextField, testDocumentField } from '~/utils/workflow';

function FieldsGroup({ entity, fields, id, nonUniqueFieldIds }) {
  const intl = useIntl();
  const count = fields.length;
  if (count === 0) {
    return null;
  }

  const entityText =
    entity === 'field'
      ? intl.formatMessage(
          {
            id: 'SetupFields.field',
            defaultMessage:
              '{count} {count, plural, one {field} other {fields}} found in the document',
          },
          { count },
        )
      : intl.formatMessage(
          {
            id: 'SetupFields.conditionalTextField',
            defaultMessage:
              '{count} {count, plural, one {conditional text field} other {conditional text fields}} found in the document',
          },
          { count },
        );

  return (
    <FlexLayout flexDirection="column" pt={8} space={4}>
      <Text color="gray-900" variant="xs-dense-medium" id={id}>
        {entityText}
      </Text>
      <ListItems columns={2}>
        {fields.map((field) => (
          <Box
            key={field.id}
            px={4}
            py={3}
            sx={{
              border: 'border',
              borderRadius: 'm',
            }}
          >
            <Field
              field={field}
              warningTooltip={
                nonUniqueFieldIds.includes(field.id)
                  ? intl.formatMessage(
                      {
                        id: 'SetupFields.nonUniqueFieldWarning',
                        defaultMessage:
                          'This field has a non-unique placeholder name ("{placeholder}")',
                        description:
                          'Warning for non-unique field placeholder. Example: placeholder= "Name"',
                      },
                      { placeholder: field.placeholder },
                    )
                  : undefined
              }
              shouldHideLabel={true}
            />
          </Box>
        ))}
      </ListItems>
    </FlexLayout>
  );
}

function Fields({ fields, nonUniqueFieldIds }) {
  const allFields = Object.values(fields);

  if (allFields.length === 0) {
    return null;
  }

  const conditionalFields = allFields.filter(testConditionalTextField);
  const placeholderFields = allFields.filter(testDocumentField);

  return (
    <FlexLayout flexDirection="column" space={8}>
      <FieldsGroup
        entity="field"
        fields={placeholderFields}
        id="workflow--placeholder-field-count"
        nonUniqueFieldIds={nonUniqueFieldIds}
      />
      <FieldsGroup
        entity="conditional text field"
        fields={conditionalFields}
        id="workflow--condition-field-count"
        nonUniqueFieldIds={nonUniqueFieldIds}
      />
    </FlexLayout>
  );
}

Fields.propTypes = {
  fields: PT.object.isRequired,
  nonUniqueFieldIds: PT.arrayOf(PT.string.isRequired),
};

export default Fields;
